import {
  Block,
  BlockNoteEditor,
  DefaultBlockSchema,
  PartialInlineContent,
  findAttachmentURLs,
} from '@packages/blocknote-core'

import classNames from 'classnames'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { type DragEvent, useCallback, useEffect, useState } from 'react'
import { Disclosure } from '@headlessui/react'
import { useBlockNote } from '@packages/blocknote-react'
import styles from '@packages/blocknote-core/extensions/Blocks/nodes/Block.module.css'
import { type Note, SourceDatabase } from '../../utils/note'
import { type SelectedDate } from '../../contexts/SelectedDate'
import { useSaveNote } from '../../hooks/useSaveNote'
import useBulletAndTaskItemCharacters from '../../hooks/useBulletAndTaskItemCharacters'
import { type WindowWithEditor } from '../../utils/WindowWithEditor'
import { useIsOnline } from '../../providers/NetworkProvider'
import { BlockContent as SharedBlockContent } from '../../components/shared/BlockRenderer'
import {
  getBlockDataAttributes,
  isBlockChecked,
  isBlockType,
  TASK_BLOCK_TYPES,
} from '../../components/shared/BlockRenderer/types'

const LOCAL_STORAGE_PREFIX = 'REFERENCES_ITEM_OPEN_STATE_'

function SkeletonLoader() {
  const lineLengths = [80, 75, 65]
  return (
    <div>
      {lineLengths.map((length, index) => {
        return (
          <div
            key={index}
            className='skeleton my-3 h-4 rounded-md'
            style={{ width: `${length.toString()}%` }}
          />
        )
      })}
    </div>
  )
}

// Converts a string into an integer
function hashValue(value: string) {
  if (typeof value !== 'string' || value.length === 0) return '0'
  let hash = 0,
    index,
    chr
  for (index = 0; index < value.length; index++) {
    chr = value.codePointAt(index) ?? 0
    hash = (hash << 5) - hash + chr
    hash = Math.trunc(hash) // Convert to 32bit integer
  }
  return hash.toString()
}

function ReferenceBlock({
  block,
  isSaving,
  onCheck,
  onBlockDrop,
}: {
  block: Block<DefaultBlockSchema>
  isSaving: boolean
  onCheck: (id: string) => void
  onBlockDrop: (event: DragEvent<HTMLDivElement>) => void
}) {
  // Now safely access the first content item
  const contentItem = Array.isArray(block.content)
    ? block.content[0]
    : block.content
  const contentText =
    typeof contentItem === 'string'
      ? contentItem
      : 'text' in contentItem
        ? contentItem.text
        : ''

  // Create a hashvalue so we just store integers in localStorage, not the actual content
  const localStorageKey = `${LOCAL_STORAGE_PREFIX}-${hashValue(contentText)}`

  // Initialize isOpen state from localStorage or default to false
  const [isOpen, setIsOpen] = useState<boolean>(
    JSON.parse(localStorage.getItem(localStorageKey) ?? 'false') as boolean
  )
  // const [isOpen, setIsOpen] = useState(false);

  // Update localStorage whenever isOpen changes
  useEffect(() => {
    if (isOpen) {
      localStorage.setItem(localStorageKey, JSON.stringify(isOpen))
    } else {
      // Remove it from localstorage to save space, because if it doesn't exists it's false by default
      localStorage.removeItem(localStorageKey)
    }
  }, [isOpen, localStorageKey])

  return (
    <div className={styles.blockGroup} data-node-type='blockGroup'>
      <div
        data-id={block.id}
        className={styles.blockOuter}
        data-node-type='block-outer'
        onDrop={(event: DragEvent<HTMLDivElement>) => {
          event.preventDefault()
          event.stopPropagation()
          event.currentTarget.dataset.dragOver = 'false'
          onBlockDrop(event)
        }}
        onDragOver={(event: DragEvent<HTMLDivElement>) => {
          event.preventDefault()
          event.stopPropagation()
          if (block.id !== 'top') {
            event.currentTarget.dataset.dragOver = 'true'
          }
        }}
        onDragLeave={(event: DragEvent<HTMLDivElement>) => {
          event.preventDefault()
          event.stopPropagation()
          event.currentTarget.dataset.dragOver = 'false'
        }}
        onDragEnd={(event: DragEvent<HTMLDivElement>) => {
          event.preventDefault()
          event.stopPropagation()
          event.currentTarget.dataset.dragOver = 'false'
        }}
        draggable={block.id !== 'top' && !isSaving}
        onDragStart={(event: DragEvent<HTMLDivElement>) => {
          event.stopPropagation()
          event.currentTarget.dataset.dragOver = 'false'
          const text = BlockNoteEditor.blocksToNotePlan([block], false)
          const payload = JSON.stringify({ id: block.id, text })
          event.dataTransfer.clearData()
          event.dataTransfer.setData('text/plain', payload)
          event.dataTransfer.effectAllowed = 'move'
        }}
      >
        <Disclosure
          as='div'
          defaultOpen={isOpen}
          className={styles.block}
          data-node-type='blockContainer'
        >
          {({ open }) => (
            <>
              <div
                className={classNames({
                  'relative flex items-center': true,
                  '-ml-5':
                    block.children.length > 0 && block.type !== 'heading',
                  '-ml-2':
                    block.children.length > 0 && block.type === 'heading',
                  'ml-2': block.children.length === 0,
                })}
              >
                {block.children.length > 0 && (
                  <Disclosure.Button
                    onClick={() => {
                      setIsOpen((previousIsOpen) => !previousIsOpen)
                    }}
                    className='absolute -top-0.5 left-0 text-left'
                  >
                    <ChevronRightIcon
                      className={classNames({
                        'rotate-90': open,
                        '-mr-1 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-200':
                          true,
                      })}
                      aria-hidden='true'
                    />
                  </Disclosure.Button>
                )}
                <div
                  className={classNames({
                    [styles.blockContent]: true,
                    'text-[13px] font-bold text-zinc-500 dark:text-zinc-400':
                      block.type === 'heading',
                    'text-[15px] dark:opacity-80': block.type !== 'heading',
                    'ml-7': block.children.length > 0,
                    'ml-0': block.children.length === 0,
                  })}
                  data-content-type={block.type}
                  {...getBlockDataAttributes(block)}
                >
                  {isBlockType(block, TASK_BLOCK_TYPES) && (
                    <label
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        onCheck(block.id)
                      }}
                      className='absolute left-0 top-0'
                    >
                      <input type='checkbox' />
                    </label>
                  )}
                  <BlockContent content={block.content} isSaving={isSaving} />
                </div>
              </div>
              <Disclosure.Panel
                className={classNames({ '-ml-3': block.type !== 'heading' })}
              >
                {block.children.map((child) => (
                  <ReferenceBlock
                    key={child.id}
                    block={child}
                    onCheck={onCheck}
                    onBlockDrop={onBlockDrop}
                    isSaving={isSaving}
                  />
                ))}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>
    </div>
  )
}

function BlockContent({
  content,
  isSaving,
}: {
  content: string | PartialInlineContent[] | undefined
  isSaving: boolean
}) {
  // Handle undefined or null content
  if (!content) {
    return (
      <span className={isSaving ? 'cursor-default' : 'cursor-grab'}> </span>
    )
  }

  if (typeof content === 'string') {
    return (
      <span className={isSaving ? 'cursor-default' : 'cursor-grab'}>
        {content}
      </span>
    )
  }

  // Use the shared BlockContent component for inline content
  return (
    <div className={isSaving ? 'cursor-default' : 'cursor-grab'}>
      <SharedBlockContent content={content} />
    </div>
  )
}

function nestBlocks(blocks: Block<DefaultBlockSchema>[]) {
  const nestedBlocks = []
  let currentBlock
  for (const block of blocks) {
    if (block.type === 'heading') {
      if (currentBlock) {
        nestedBlocks.push(currentBlock)
      }
      currentBlock = block
    } else if (isBlockType(block, [...TASK_BLOCK_TYPES, 'bulletListItem'])) {
      if (!currentBlock) {
        currentBlock = {
          id: 'top',
          type: 'heading',
          content: [{ type: 'text', text: 'Top Items', styles: {} }],
          children: [],
          props: {
            level: '1',
            backgroundColor: 'transparent',
            textColor: 'black',
            textAlignment: 'left',
            flagged: 0,
            isParentBlock: false,
            isChildBlock: false,
            folded: false,
            visible: true,
          },
        } as Block<DefaultBlockSchema>
      }
      currentBlock.children.push(block)
    }
  }
  if (currentBlock) {
    nestedBlocks.push(currentBlock)
  }
  return nestedBlocks
}

// Block types that are valid for week note references
const REFERENCE_VALID_TYPES = [
  'taskListItem',
  'checkListItem',
  'quoteListItem',
  'bulletListItem',
  'heading',
] as const

function isValidBlock(block: Block<DefaultBlockSchema>) {
  return isBlockType(block, REFERENCE_VALID_TYPES) && !isBlockChecked(block)
}

function recursiveBlockFilter(
  blocks: Block<DefaultBlockSchema>[]
): Block<DefaultBlockSchema>[] {
  return blocks
    .filter((element) => isValidBlock(element))
    .map((block) => {
      block.children = recursiveBlockFilter(block.children)
      return block
    })
}

export function WeekNoteReference({
  isLoading,
  weekNote,
  selectedDate,
  jumpToWeek,
}: {
  isLoading: boolean
  weekNote?: Note
  selectedDate: SelectedDate
  jumpToWeek: (_selectedDate: SelectedDate) => void
}) {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const [blocks, setBlocks] = useState<Block<DefaultBlockSchema>[]>([])
  const { taskItemCharacters, bulletItemCharacters } =
    useBulletAndTaskItemCharacters()
  const [isSaving, setIsSaving] = useState(false)
  const saveNote = useSaveNote((_note) => {
    setIsSaving(false)
  })
  const isOnline = useIsOnline()

  useEffect(() => {
    if (!weekNote) {
      return
    }
    const localStorageKey = `${LOCAL_STORAGE_PREFIX}-${weekNote.filename}`
    setIsOpen(
      JSON.parse(localStorage.getItem(localStorageKey) ?? 'false') as boolean
    )
  }, [weekNote])

  const initialContent = BlockNoteEditor.notePlanToBlocks(
    weekNote?.content ?? '',
    ''
  )
  const editor: BlockNoteEditor | null = useBlockNote(
    {
      initialContent,
      bulletItemCharacters,
      taskItemCharacters,
      onEditorReady: (currentEditor: BlockNoteEditor) => {
        updateBlocks(currentEditor.topLevelBlocks)
      },
      onEditorContentChange(currentEditor: BlockNoteEditor) {
        const currentBlocks = currentEditor.topLevelBlocks
        updateBlocks(currentBlocks)
        const content = BlockNoteEditor.blocksToNotePlan(
          currentBlocks,
          weekNote?.source === SourceDatabase.CLOUDKIT
        )
        setIsSaving(true)
        if (weekNote?.filename) {
          saveNote.mutate({
            content,
            attachments: findAttachmentURLs(currentEditor._tiptapEditor),
            filename: weekNote.filename,
            recordName: weekNote.recordName,
            // eslint-disable-next-line unicorn/no-null -- supabase uses null
            parent: weekNote.parent ?? null,
            noteType: weekNote.noteType,
            modificationDate: new Date(),
          })
        }
      },
    },
    [
      weekNote?.recordChangeTag,
      weekNote?.filename,
      bulletItemCharacters,
      taskItemCharacters,
    ]
  )

  function updateBlocks(currentBlocks: Block<DefaultBlockSchema>[]) {
    // it's important to make a copy of the blocks here, otherwise the editor will be mutated
    const blocksCopy = recursiveBlockFilter(structuredClone(currentBlocks))
    // if there are headings, nest the blocks
    if (blocksCopy.some((block) => block.type === 'heading')) {
      setBlocks(nestBlocks(blocksCopy))
    } else {
      setBlocks(blocksCopy)
    }
  }

  function handleOnCheck(id: string) {
    // find the block in the editor and toggle the checked prop
    const block = editor?.getBlock(id)
    if (block) {
      editor?.updateBlock(id, {
        props: { checked: !isBlockChecked(block) },
      })
    }
  }

  const handleBlockDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const target = event.currentTarget as HTMLElement
      if (target.dataset.id && target.dataset.id !== 'top') {
        const html = event.dataTransfer.getData('text/html')
        if (html) {
          const element = document.createElement('div')
          element.innerHTML = html
          const blocksToAppend: Block<DefaultBlockSchema>[] = []
          for (const node of element.childNodes) {
            const id = (node as HTMLElement).dataset.id
            if (id) {
              const block: Block<DefaultBlockSchema> | undefined = (
                window as WindowWithEditor
              ).editor?.getBlock(id)
              if (block) {
                blocksToAppend.push(block)
              }
            }
          }
          // add the block to the new position
          editor?.insertBlocks(blocksToAppend, target.dataset.id, 'after')
          // remove the block from the current position
          ;(window as WindowWithEditor).editor?.removeBlocks(
            blocksToAppend.map((block) => block.id)
          )
        }
      }
    },
    // editor is needed here to have always the latest block ids
    [editor]
  )

  useEffect(
    () => {
      function handleWindowMessage(event: MessageEvent) {
        // Type guard to ensure event.data has the expected structure
        const eventData: unknown = event.data
        if (
          eventData &&
          typeof eventData === 'object' &&
          'type' in eventData &&
          eventData.type === 'droppedWeekReferenceBlock' &&
          'id' in eventData &&
          typeof eventData.id === 'string'
        ) {
          // Extract the id with proper typing
          const blockId: string = eventData.id
          editor?.removeBlocks([blockId])
        }
      }

      window.addEventListener('message', handleWindowMessage)

      return () => {
        window.removeEventListener('message', handleWindowMessage)
      }
    }, // editor is needed here to have always the latest block ids
    [editor]
  )

  if (!weekNote) {
    return
  }

  return (
    <div className={classNames('relative', { 'opacity-10': !isOnline })}>
      <Disclosure>
        <Disclosure.Button
          as='h1'
          onClick={() => {
            setIsOpen((previousIsOpen) => !previousIsOpen)
          }}
          className='blocknote-padding mx-auto mt-3 flex w-full max-w-3xl cursor-default items-center justify-between opacity-60'
        >
          <div className='flex w-full justify-between'>
            <div className='flex items-center'>
              <ChevronRightIcon
                className={classNames({
                  'rotate-90': isOpen,
                  'mr-1 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-200':
                    true,
                })}
                aria-hidden='true'
              />
              <span className='text-xs font-bold uppercase opacity-60'>
                Week {selectedDate.week}
              </span>
            </div>
            <i
              role='button'
              tabIndex={0}
              className='fas fa-arrow-right cursor-pointer text-xs'
              onClick={() => {
                jumpToWeek(selectedDate)
              }}
            />
          </div>
        </Disclosure.Button>
        <Disclosure.Panel>
          <div className='blocknote-padding mx-auto w-full max-w-3xl cursor-default'>
            <div className='my-3 rounded-md bg-zinc-100/70 p-2 pt-0 dark:bg-zinc-800'>
              {isLoading ? (
                <SkeletonLoader />
              ) : (
                <div
                  className={classNames([
                    'relative',
                    isSaving ? 'opacity-70' : 'opacity-100',
                  ])}
                >
                  {isSaving ? (
                    <i className='fas fa-spinner-third fa-spin absolute right-0 top-0 mr-2 mt-2' />
                  ) : undefined}
                  {blocks.map((block) => (
                    <ReferenceBlock
                      key={block.id}
                      block={block}
                      onCheck={handleOnCheck}
                      onBlockDrop={handleBlockDrop}
                      isSaving={isSaving}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Disclosure.Panel>
      </Disclosure>
      {!isOnline && <div className='absolute left-0 top-0 h-full w-full' />}
    </div>
  )
}
